/**
 * The phone's Month — Aaron, Oct 25, phone note 3.2: "a month view that is
 * scrollable and shoves every entry into a single line with colors", after
 * Outlook's phone calendar (`docs/activity.md` §10.2).
 *
 * Month after month in one scroller, the past above and the future below,
 * each month under its own sticky heading (*October 2026*) and its days
 * Sunday first under the fixed *S M T W T F S*. A day lists what is on it as
 * single coloured lines — the kind's colour with one person shown (a site
 * visit green, a meeting blue, a task amber, a block grey, as the grid's
 * edges are), the person's with several — its title cut short, as many as the
 * cell holds and then *+n*. Today is the solid accent circle, the selected
 * day ringed, a day gone a shade greyer (§6.1) and a day nobody shown works
 * on the `surface-alt` of the desktop's Month. The forecast's icon sits on
 * today and the six days after, as on the phone's strip (§6.9).
 *
 * A tap anywhere on a day picks it (`onPickDay` — the shell then opens Day);
 * a line is not a target of its own, because at 15 px it is a third of a
 * finger.
 */
import { memo, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  KIND_STYLES, MONTH_CELL_LINES, WEATHER_COLOURS, WEATHER_TITLES, WEEKDAY_LETTERS, addMonthKey, agendaHeadingWords,
  entriesOn, fitLines, monthCells, monthKeyOf, monthTitle, toneOf,
  type CalendarAnswer, type CalendarItem, type DayEntry,
} from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { useActivity } from '../activity-context.js'
import { Glyph } from '../glyphs.js'
import { PAST_TINT } from './look.js'
import { useCalendarMonths } from './use-months.js'
import { useEndless } from './use-endless.js'

export interface PhoneMonthProps {
  /** The selected day, `YYYY-MM-DD` (`state.calendar.date`). */
  date: string
  /** The people shown, in the people button's order. */
  people: string[]
  /** A day tapped. */
  onPickDay: (date: string) => void
  /** The fixed *S M T W T F S* over the months; off when the shell draws its own. */
  weekdays?: boolean
}

/** Months drawn either side of the one opened on, then added three at a time, at most eighteen. */
const AROUND = { before: 2, after: 3 }
const STEP = 3
const MOST = 18

/** The colour a line wears: the kind's with one person, the person's with several (§6.2, §6.4.2). */
export function lineColour(item: CalendarItem, person: { colour: string } | null): string {
  if (person) return toneOf(person.colour).edge
  if (item.kind === 'appointment') return KIND_STYLES.site_visit.fg
  if (item.kind === 'meeting') return KIND_STYLES.meeting.fg
  if (item.kind === 'task') return KIND_STYLES.task.fg
  return KIND_STYLES.block.fg
}

/** A line's fill: its colour over the surface, light in either theme (as a mark's tint is). */
const tintOf = (colour: string) => `color-mix(in srgb, ${colour} 17%, rgb(var(--color-surface)))`

const lineTitle = (item: CalendarItem) => (item.allDay ? 'Time off' : item.title)

export function PhoneMonth({ date, people, onPickDay, weekdays = true }: PhoneMonthProps) {
  const { state, today } = useActivity()
  const show = state.calendar.show
  const opened = monthKeyOf(date)
  const [range, setRange] = useState({ from: addMonthKey(opened, -AROUND.before), to: addMonthKey(opened, AROUND.after) })
  const [top, setTop] = useState(opened)
  const months = useMemo(() => {
    const out: string[] = []
    for (let month = range.from; month <= range.to; month = addMonthKey(month, 1)) out.push(month)
    return out
  }, [range])
  /* The months on screen and either side of them, asked for as they come into view. */
  const near = useMemo(() => [addMonthKey(top, -1), top, addMonthKey(top, 1), addMonthKey(top, 2)], [top])
  const { answers, errors } = useCalendarMonths(people, near, months)

  /* One function for the life of the view, so a month that has not changed is not drawn again. */
  const pick = useRef(onPickDay)
  pick.current = onPickDay
  const pickDay = useCallback((day: string) => pick.current(day), [])

  const scroller = useRef<HTMLDivElement>(null)
  const { scrollToKey } = useEndless(scroller, {
    attr: 'data-month',
    margin: 1200,
    onTopChange: setTop,
    onNearTop: () => setRange((was) => {
      const from = addMonthKey(was.from, -STEP)
      const to = monthsFrom(from, was.to) > MOST ? addMonthKey(from, MOST - 1) : was.to
      return { from, to }
    }),
    onNearBottom: () => setRange((was) => {
      const to = addMonthKey(was.to, STEP)
      const from = monthsFrom(was.from, to) > MOST ? addMonthKey(to, -(MOST - 1)) : was.from
      return { from, to }
    }),
  })

  /*
   * Open on the selected day's month, and bring it back when the day is
   * picked elsewhere: a month outside the window recentres it, and the
   * scroller puts the month at the top as soon as it is drawn.
   */
  useLayoutEffect(() => {
    const month = monthKeyOf(date)
    setRange((was) => (month < was.from || month > was.to
      ? { from: addMonthKey(month, -AROUND.before), to: addMonthKey(month, AROUND.after) } : was))
    setTop(month)
    scrollToKey(month)
  }, [date, scrollToKey])

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-surface [line-height:normal]" data-phone-view="month">
      {weekdays && people.length > 0 && (
        <div className="grid shrink-0 grid-cols-7 border-b border-line bg-surface" aria-hidden>
          {WEEKDAY_LETTERS.map((letter, index) => (
            <span key={index} className="py-1.5 text-center text-[11px] font-semibold text-text-muted">{letter}</span>
          ))}
        </div>
      )}
      {/* One scroller whoever is shown, so the months keep listening to it when the people change. */}
      <div ref={scroller} className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain [overflow-anchor:none]">
        {people.length === 0 && <div className="p-4 text-[13px] text-text-muted">Unassigned has no calendar.</div>}
        {people.length > 0 && months.map((month) => (
          <MonthBlock key={month} month={month} answer={answers.get(month) ?? null} error={errors[month] ?? null}
            date={date} today={today} show={show} several={people.length > 1} onPickDay={pickDay} />
        ))}
      </div>
    </div>
  )
}

const monthsFrom = (from: string, to: string): number => {
  const [fy, fm] = from.split('-').map(Number) as [number, number]
  const [ty, tm] = to.split('-').map(Number) as [number, number]
  return (ty - fy) * 12 + (tm - fm) + 1
}

/** A month: drawn again only when its own answer, the selected day or Show changes. */
const MonthBlock = memo(function MonthBlock({
  month, answer, error, date, today, show, several, onPickDay,
}: {
  month: string; answer: CalendarAnswer | null; error: string | null; date: string; today: string
  show: { visits: boolean; tasks: boolean; blocks: boolean }; several: boolean; onPickDay: (date: string) => void
}) {
  return (
    <section data-month={month}>
      <h2 className="sticky top-0 z-[2] m-0 flex h-9 items-center gap-2 border-b border-line-faint bg-surface px-3.5 text-[15px] font-bold">
        {monthTitle(month)}
        {error && <span className="truncate text-[11.5px] font-medium text-danger">{error}</span>}
      </h2>
      {monthCells(month).map((week, index) => (
        <div key={index} className="grid grid-cols-7">
          {week.map((day, column) => (day ? (
            <DayCell key={day} day={day} answer={answer} selected={day === date} today={today} show={show}
              several={several} first={column === 0} onPick={() => onPickDay(day)} />
          ) : (
            <span key={`empty-${column}`} aria-hidden
              className={cn('h-[92px] border-t border-line-faint', column > 0 && 'border-l')} />
          )))}
        </div>
      ))}
    </section>
  )
})

function DayCell({
  day, answer, selected, today, show, several, first, onPick,
}: {
  day: string; answer: CalendarAnswer | null; selected: boolean; today: string
  show: { visits: boolean; tasks: boolean; blocks: boolean }; several: boolean; first: boolean; onPick: () => void
}) {
  const entries: DayEntry[] = entriesOn(answer, day, show)
  const { shown, more } = fitLines(entries.length, MONTH_CELL_LINES)
  const gone = day < today
  const isToday = day === today
  /* A day nobody shown works on is shaded, as the desktop's Month shades it (§6.4.3). */
  const off = answer ? answer.people.length > 0 && answer.people.every((person) => {
    const own = person.days.find((d) => d.date === day)
    return own ? !own.working : false
  }) : false
  const weather = answer?.dates.find((d) => d.date === day)?.weather ?? null
  const count = entries.length
  return (
    <button type="button" onClick={onPick} data-day={day} aria-pressed={selected}
      aria-current={isToday ? 'date' : undefined}
      aria-label={`${agendaHeadingWords(day, today)}${count > 0 ? ` · ${count} ${count === 1 ? 'thing' : 'things'}` : ''}`}
      className={cn('relative flex h-[92px] min-w-0 flex-col items-stretch justify-start overflow-hidden border-t border-line-faint px-0.5 pb-0.5 pt-1 text-left',
        !first && 'border-l', off && 'bg-surface-alt')}
      style={gone ? { boxShadow: `inset 0 0 0 999px ${PAST_TINT}` } : undefined}>
      <span className="mb-[3px] flex h-5 items-center gap-0.5 pl-px">
        <b className={cn('inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[12px] font-bold',
          isToday ? 'bg-accent text-white' : selected ? 'border-[1.5px] border-accent text-accent-dark'
            : gone ? 'text-text-muted' : 'text-text')}>
          {Number(day.slice(8))}
        </b>
        <span className="flex-1" />
        {/* The icon alone, as on the phone's strip (§6.9): a 56 px cell has no room for the high beside the date. */}
        {weather && (
          <span className="inline-flex pr-0.5" data-forecast="" style={{ color: WEATHER_COLOURS[weather.icon] }}>
            <Glyph name={weather.icon} size={12} title={WEATHER_TITLES[weather.icon]} />
          </span>
        )}
      </span>
      <span className="flex min-w-0 flex-col gap-px">
        {entries.slice(0, shown).map(({ item, person }) => {
          const colour = lineColour(item, several ? person : null)
          return (
            <span key={item.key} data-line={item.kind}
              className={cn('block h-[15px] min-w-0 truncate whitespace-nowrap rounded-[3px] pl-[4px] pr-0.5 text-[10px] font-semibold leading-[15px]',
                /* A done task is struck through; a done visit stays as it was (§6.2). */
                item.kind === 'task' && item.done ? 'text-text-faint line-through' : 'text-text')}
              style={{ background: tintOf(colour), boxShadow: `inset 2px 0 0 ${colour}` }}>
              {lineTitle(item)}
            </span>
          )
        })}
        {more > 0 && (
          <span data-more="" className="block h-[15px] pl-[4px] text-[10px] font-semibold leading-[15px] text-text-muted">+{more}</span>
        )}
      </span>
    </button>
  )
}
